/**
 * Scale profile (WORKBOARD S1, SCALE.md): one world, one scale, and the layer is the camera
 * distance. Nothing is resized one constant at a time — every scale-bearing number that belongs
 * to a *person* derives from the profile here, and `classic` reproduces today's constants exactly
 * (tests/scaleProfile.test.ts; the headless golden is the proof for the turn match).
 *
 * Two observers (WORKBOARD §7.2): a **person** (layer 0 — on foot, the handheld, the action clock)
 * and a **vehicle** (layer 2 — today's 1.1 u unit re-read as the crewed walker, the mounted shot).
 * `kHuman` scales the person set only. The vehicle set — LOS_EYE_HEIGHT, MELEE_RANGE, INTERACT_RANGE,
 * BASE_MOVEMENT_RANGE, JUMP_VELOCITY_PER_MOVEMENT, the perception ranges and cone, COVER_FULL_RIM and
 * the cover arcs, the sneaking ring, patrol arcs, the fire-search grid — stays the constants it is,
 * which is what keeps every golden and sweep valid (k_planet = 1 is a ruling, not a dial).
 *
 * Felt gravity (WORKBOARD §8.1, ruling 15): people and what they throw feel g × `personGravity`,
 * so every person-scale length shrinks by k while every time stays — the duel plays identically
 * in person-units at any k. The factor is exposed here; the action clock applies it.
 */

import type { GameConfig, ScaleProfileName } from '../types';
import { PERSON_BODY_HEIGHT, TOWER_PERSON_HEIGHTS } from '../constants/physics';
import { PLAYER_HITBOX_RADIUS } from './physics';

/** A classic person's height (u) — the body model's head-and-helmet top (SCALE.md §1). */
export { PERSON_BODY_HEIGHT } from '../constants/physics';   // (R12: defined with the physics constants so the body table can read it)
/** An information tower is two people tall (owner, WORKBOARD §12 ruling 2). */
export { TOWER_PERSON_HEIGHTS } from '../constants/physics';
/** I2 drone, classic values (× k_human for lengths): 3× a person's action walk, sixteen people up (65.42; eight before), a cone ~ as wide as it is high, a 0.4 u body, 15 s of flight. */
export const DRONE_SPEED = 15;
/**
 * 65.42 (the owner, Oct 18: "For drone, I think fly higher (16) accept view area change, 2 hits to bring down"): sixteen
 * people up (eight before: shot down 0.58 a launch), its cone of sight growing with it — 16 people round its ground point
 * (8 before), as wide as it is high — and DRONE_HITS bullets to bring it down.
 */
export const DRONE_ALTITUDE = 16 * PERSON_BODY_HEIGHT;
export const DRONE_SIGHT_RADIUS = 16 * PERSON_BODY_HEIGHT;
export const DRONE_HIT_RADIUS = 0.4;
/** 65.42: the bullets that bring a drone down (a burst, any blow other than a bullet, still downs it at once). */
export const DRONE_HITS = 2;
export const DRONE_ENDURANCE = 15;
/** I2: seconds a launch holds the operator still (owner: busy while tasking and launching, not after). */
export const DRONE_LAUNCH_TIME = 1;
/**
 * Decision 11, the micro-relief a person walks on: hummocks up to 1.2 person-heights (enough to hide
 * someone lying in a fold, not someone standing), over a broad octave 6 person-heights across with a
 * finer one at 2. In person-heights so it scales with the person; off unless the profile says so.
 * 67.50 (decision 155): the Lab's Fine relief row (personTuning.fineRelief) tries 0.6 (the tallest bump under a
 * standing eye) or 0 (no fine ground: people on the coarse surface, nothing drawn) against this.
 */
export const FINE_AMPLITUDE_HEIGHTS = 1.2;
export const FINE_WAVELENGTH_HEIGHTS = 6;
/**
 * The person-scale battle layout (WORKBOARD §13: "distance binds before knowledge"). With a person
 * layout, an action battle keeps the classic deploy's geometry in person units: every unit's distance
 * from the battle's centre shrinks by k (core/actionSetup.ts layOutFront). A setup may instead ask for
 * an explicit front this many person-heights wide (`frontHeights`); 100 ≈ a classic megaplanet
 * skirmish's typical spacing.
 */
export const FRONT_SPAN_HEIGHTS = 100;
/** Units of a team stand this far apart when laid out (the action setup's classic spacing, 1.6 u). */
export const DEPLOY_SPACING = 1.6;
import {
  ACTION_WALK_SPEED, ACTION_JUMP_HEIGHT, ACTION_UNIT_RADIUS, ACTION_MAX_SLOPE, ACTION_SCRAMBLE, ACTION_HEAR_RANGE, ACTION_EYE_HEIGHT,
  BULLET_SPEED, BULLET_DAMAGE, BULLET_LIFE, ACTION_FIRE_INTERVAL, BULLET_SPREAD, BULLET_GRAVITY_SCALE, BULLET_SUBSTEP,
  ACTION_STEP_OVER, POINTS_CAPTURE_RADIUS, ACTION_RESPAWN_WAVE, LOS_EYE_HEIGHT, INTERACT_RANGE,
} from '../constants';

export interface ScaleProfile {
  name: ScaleProfileName;
  /** Person-scale lengths × this (1 = today's 1.1 u person; 1/20 = the ruling's 1 : 100 on a mixed planet). */
  kHuman: number;
  /** Gravity a person (and a handheld shot) feels, as a fraction of the planet's g. Default = kHuman (ruling 15a). */
  personGravity: number;
  /** Decision 11: a person-scale micro-relief over the planet mesh, read by person-side queries only (core/fineTerrain.ts). Off at classic. */
  fineTerrain: boolean;
  /** WORKBOARD §13 / S3: lay action battles out as a person-scale front (core/actionSetup.ts layOutFront). Off at classic. */
  personLayout: boolean;
  /** 67.13: × the felt gravity a bullet falls by (owner, Oct 10: "I really like the visible bullet drop", but less of it — "maybe .60x"). */
  bulletGravity: number;
  /** 67.15: × every bullet's muzzle speed (owner, Oct 10: "1.5 or 2 both work better than 1"; then "let's make bullet velocity 2 the default"). */
  bulletSpeed: number;
}

export const SCALE_PROFILES: Record<ScaleProfileName, ScaleProfile> = {
  classic: { name: 'classic', kHuman: 1, personGravity: 1, fineTerrain: false, personLayout: false, bulletGravity: 1, bulletSpeed: 1 },
  human: { name: 'human', kHuman: 1 / 20, personGravity: 1 / 20, fineTerrain: true, personLayout: true, bulletGravity: 0.6, bulletSpeed: 2 },
};

export const DEFAULT_SCALE: ScaleProfileName = 'classic';

/** Which kind of thing is asking: a person on foot, or the vehicle the turn match moves. */
export type Observer = 'person' | 'vehicle';

/**
 * The person set: every scale-bearing number the action clock reads, derived from the profile.
 * Lengths × kHuman; times, ratios, counts and damage unchanged (felt gravity keeps the times).
 */
export interface PersonSet {
  walkSpeed: number;        // u/s — a length per second; time unchanged → × k
  jumpHeight: number;       // u
  unitRadius: number;       // u
  maxSlope: number;         // ratio — unchanged
  scramble: number;         // ratio — unchanged: decision 134, the share of the pace kept scrambling up ground steeper than maxSlope
  hearRange: number;        // u
  eyeHeight: number;        // u — the action eye / muzzle
  losEyeHeight: number;     // u — the person's eye for line-of-sight (the vehicle's mast stays LOS_EYE_HEIGHT)
  bulletSpeed: number;      // u/s
  /** 67.13: × every bullet's muzzle speed (the rifle's is in bulletSpeed already; the pistol's reads this) — the profile's (human 2), or the tuning's. */
  bulletSpeedScale: number;
  bulletDamage: number;     // unchanged
  bulletLife: number;       // s — unchanged
  fireInterval: number;     // s — unchanged
  bulletSpread: number;     // radians — unchanged
  bulletGravityScale: number; // unchanged (felt gravity is applied by the clock, not here)
  bulletSubstep: number;    // u
  stepOver: number;         // u
  captureRadius: number;    // u — people hold a point; a point is person-sized (tagged person, WORKBOARD §9.1)
  respawnWave: number;      // s — unchanged
  cameraBack: number;       // u — the over-the-shoulder boom
  cameraUp: number;
  cameraRight: number;
  // Lane C (felt gravity + the rest of the action lengths, WORKBOARD §8.6) — all × k
  /** The person length factor itself (kHuman), for a one-off length written as `literal × k`. */
  k: number;
  hitboxRadius: number;     // u — the person's hit sphere (bullets)
  hitboxCenter: number;     // u — its centre above the feet
  towerEyeHeight: number;   // u — an information tower's eye: two person-heights (WORKBOARD I1, owner: humble)
  droneSpeed: number;       // u/s — WORKBOARD I2 (a length per second; time unchanged → × k)
  droneAltitude: number;    // u — above the terrain under it (owner: low, so it reveals less than the probe)
  droneSightRadius: number; // u — the ground radius of its cone of sight
  droneHitRadius: number;   // u — what a bullet must pass within to bring it down
  droneEndurance: number;   // s — unchanged with k
  droneLaunchTime: number;  // s — unchanged with k: how long a launch holds the operator
  fineAmplitude: number;    // u — decision 11: the tallest micro-relief bump (0 = off, classic)
  fineWavelength: number;   // u — the micro-relief's broad octave; a finer one rides at a third of it
  personLayout: boolean;    // lay action battles out at person scale (off at classic: the deploy is untouched)
  deploySpacing: number;    // u — how far apart a laid-out team's units stand
  jumpLiftoff: number;      // u — a hop leaves the ground this far up
  coverSampleStep: number;  // u — sight / shots sample cover this often
  botPreferredMin: number;  // u — the action bot's fight band
  botPreferredMax: number;
  botCoverSeek: number;     // u — how far a hurt bot goes for cover
  botMinCoverHeight: number; // u — cover that hides the whole hitbox
  botArrive: number;        // u — "got there"
  /** 64.11: how near a person must stand to get into a seat (INTERACT_RANGE × k). */
  seatReach: number;        // u
  botSearchNear: number;    // u — with an objective, chase only what is this close
  botAimLift: number;       // u — aim this far up when a rise hides the body
  /** Gravity a person and their shots feel, × the planet's g (felt gravity, ruling 15a). */
  gravityScale: number;
  /** Extra spread on a shot fired while moving, × the move fraction (radians; 0 = today — PersonTuning.moveSpread). */
  moveSpread: number;
}

const CAMERA_BACK = 3.2, CAMERA_UP = 0.55, CAMERA_RIGHT = 0.75;

function derive(p: ScaleProfile): PersonSet {
  const k = p.kHuman;
  return {
    walkSpeed: ACTION_WALK_SPEED * k,
    jumpHeight: ACTION_JUMP_HEIGHT * k,
    unitRadius: ACTION_UNIT_RADIUS * k,
    maxSlope: ACTION_MAX_SLOPE,
    scramble: ACTION_SCRAMBLE,
    hearRange: ACTION_HEAR_RANGE * k,
    eyeHeight: ACTION_EYE_HEIGHT * k,
    losEyeHeight: LOS_EYE_HEIGHT * k,
    bulletSpeed: BULLET_SPEED * k * p.bulletSpeed,
    bulletDamage: BULLET_DAMAGE,
    bulletLife: BULLET_LIFE,
    fireInterval: ACTION_FIRE_INTERVAL,
    bulletSpread: BULLET_SPREAD,
    bulletGravityScale: BULLET_GRAVITY_SCALE * p.bulletGravity,
    bulletSpeedScale: p.bulletSpeed,
    bulletSubstep: BULLET_SUBSTEP * k,
    stepOver: ACTION_STEP_OVER * k,
    captureRadius: POINTS_CAPTURE_RADIUS * k,
    respawnWave: ACTION_RESPAWN_WAVE,
    cameraBack: CAMERA_BACK * k,
    cameraUp: CAMERA_UP * k,
    cameraRight: CAMERA_RIGHT * k,
    k,
    hitboxRadius: PLAYER_HITBOX_RADIUS * k,
    hitboxCenter: 0.5 * k,
    towerEyeHeight: TOWER_PERSON_HEIGHTS * PERSON_BODY_HEIGHT * k,
    droneSpeed: DRONE_SPEED * k,
    droneAltitude: DRONE_ALTITUDE * k,
    droneSightRadius: DRONE_SIGHT_RADIUS * k,
    droneHitRadius: DRONE_HIT_RADIUS * k,
    droneEndurance: DRONE_ENDURANCE,
    droneLaunchTime: DRONE_LAUNCH_TIME,
    fineAmplitude: p.fineTerrain ? FINE_AMPLITUDE_HEIGHTS * PERSON_BODY_HEIGHT * k : 0,
    personLayout: p.personLayout,
    deploySpacing: DEPLOY_SPACING * k,
    fineWavelength: FINE_WAVELENGTH_HEIGHTS * PERSON_BODY_HEIGHT * k,
    jumpLiftoff: 0.05 * k,
    coverSampleStep: 0.25 * k,
    botPreferredMin: 6 * k,
    botPreferredMax: 16 * k,
    botCoverSeek: 7 * k,
    botMinCoverHeight: 1.1 * k,
    botArrive: 1.5 * k,
    seatReach: INTERACT_RANGE * k,
    botSearchNear: 12 * k,
    botAimLift: 0.35 * k,
    gravityScale: p.personGravity,
    moveSpread: 0,
  };
}

const cache = new Map<ScaleProfileName, PersonSet>();

export function scaleProfile(name: ScaleProfileName | undefined): ScaleProfile {
  return SCALE_PROFILES[name ?? DEFAULT_SCALE] ?? SCALE_PROFILES.classic;
}

/** The person set for a profile (memoised; a plain object, never mutated). */
export function personSet(name: ScaleProfileName | undefined): PersonSet {
  const key = name ?? DEFAULT_SCALE;
  let ps = cache.get(key);
  if (!ps) { ps = derive(scaleProfile(key)); cache.set(key, ps); }
  return ps;
}

/** The person set a match runs under (`config.scale`, default classic). */
export function personSetOf(state: { config: Pick<GameConfig, 'scale' | 'personTuning'> }): PersonSet {
  const base = personSet(state.config.scale);
  const t = state.config.personTuning;
  if (!t) return base;
  // 62.57 cut 5: the tuned set kept on the tuning object (a match's config keeps one), so a call builds no key — the
  // string key below is only for a tuning object first seen
  const byScale = tunedByObject.get(t);
  const hit = byScale?.get(state.config.scale ?? DEFAULT_SCALE);
  if (hit) return hit;
  const key = `${state.config.scale ?? DEFAULT_SCALE}|${t.bulletDamage ?? ''}|${t.fireInterval ?? ''}|${t.respawnWave ?? ''}|${t.moveSpread ?? ''}|${t.fineAmplitude ?? ''}|${t.fineRelief ?? ''}|${t.bulletGravity ?? ''}|${t.bulletSpeed ?? ''}|${t.scramble ?? ''}`;
  let ps = tunedCache.get(key);
  if (!ps) {
    ps = { ...base, ...(t.bulletDamage !== undefined ? { bulletDamage: t.bulletDamage } : {}), ...(t.fireInterval !== undefined ? { fireInterval: t.fireInterval } : {}), ...(t.respawnWave !== undefined ? { respawnWave: t.respawnWave } : {}), ...(t.moveSpread !== undefined ? { moveSpread: t.moveSpread } : {}), ...(t.fineRelief !== undefined && base.fineAmplitude > 0 ? { fineAmplitude: t.fineRelief * PERSON_BODY_HEIGHT * base.k } : {}), ...(t.fineAmplitude !== undefined ? { fineAmplitude: t.fineAmplitude } : {}), ...(t.bulletGravity !== undefined ? { bulletGravityScale: BULLET_GRAVITY_SCALE * t.bulletGravity } : {}), ...(t.bulletSpeed !== undefined ? { bulletSpeed: (base.bulletSpeed / base.bulletSpeedScale) * t.bulletSpeed, bulletSpeedScale: t.bulletSpeed } : {}), ...(t.scramble !== undefined ? { scramble: t.scramble } : {}) };
    tunedCache.set(key, ps);
  }
  if (byScale) byScale.set(state.config.scale ?? DEFAULT_SCALE, ps);
  else tunedByObject.set(t, new Map([[state.config.scale ?? DEFAULT_SCALE, ps]]));
  return ps;
}
/** 62.57 cut 5: the tuned sets by the tuning object itself, then the scale (the same sets the key's cache holds). */
const tunedByObject = new WeakMap<object, Map<string, PersonSet>>();
/** Decision 24 experiments: a tuned person set per (scale, tuning). */
const tunedCache = new Map<string, PersonSet>();

/** Line-of-sight eye height for an observer: the vehicle's mast is the constant; the person's eye scales. */
export function eyeHeightFor(observer: Observer, name: ScaleProfileName | undefined): number {
  return observer === 'vehicle' ? LOS_EYE_HEIGHT : personSet(name).losEyeHeight;
}

/** Gravity felt by a body of this kind, as a fraction of the planet's g (ruling 15a: people feel g × kHuman). */
export function gravityScaleFor(observer: Observer, name: ScaleProfileName | undefined): number {
  return observer === 'vehicle' ? 1 : scaleProfile(name).personGravity;
}
